import * as React from 'react';
import { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { API } from 'aws-amplify';
import { ArrowLeft, Grid3X3, List, Plus } from 'lucide-react';
import { PageHeader } from '../../components/page-header';
import { Button } from '../../components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { useToast } from '../../components/ui/toast';
import { usePermissions } from '../../../application/context/PermissionsContext/PermissionsContext';
import { PermissionKeys } from '../../../application/context/PermissionsContext/permissionTypes';
import { LessonCard } from './components/LessonCard';
import { UploadLessonModal } from './components/UploadLessonModal';
import { getLessons } from './TrainingHomePage';
import { cn } from '../../lib/utils';

declare const global: {
  me: {
    id: string;
    orgID: string;
    isAdmin: boolean;
    imageName?: string;
    name: string;
  };
  trainingVideos: Record<string, any[]> | null;
};

interface Lesson {
  id: string;
  name: string;
  description: string;
  youTubeURL: string | null;
  url: string;
  urlType: string;
  order: number;
  imageName: string;
  imageType: string;
  posterImageName: string | null;
  category: string;
  createdAt: string;
  videoURL?: string;
}

export default function TrainingCategoryPage() {
  const navigate = useNavigate();
  const { category } = useParams<{ category: string }>();
  const orgID = global.me.orgID;
  const toast = useToast();
  const { checkPermission } = usePermissions();

  const canManage = checkPermission(PermissionKeys.Other_ManageTrainingVideos);

  const [lessons, setLessons] = useState<Lesson[] | null>(null);
  const [displayStyle, setDisplayStyle] = useState<'grid' | 'list'>('grid');
  const [sortAsc, setSortAsc] = useState(true);
  const [uploadVid, setUploadVid] = useState(false);
  const [mediaType, setMediaType] = useState<'video' | 'document'>('video');

  const refresh = useCallback(() => {
    getLessons(orgID, API, (res) => {
      if (res && category && res[category] && res[category].length) {
        setLessons(res[category]);
      } else {
        navigate('/v2/training');
      }
    });
  }, [orgID, category, navigate]);

  useEffect(() => {
    if (!category) {
      return;
    }
    if (global.trainingVideos) {
      setLessons(global.trainingVideos[category] || []);
    } else {
      getLessons(orgID, API, (res) => {
        if (res && res[category]) {
          setLessons(res[category]);
        } else {
          setLessons([]);
        }
      });
    }
  }, [category, orgID]);

  const sortedLessons = React.useMemo(() => {
    if (!lessons) {
      return null;
    }
    const filtered = lessons.filter((lesson) =>
      mediaType === 'document'
        ? lesson.imageType === 'pdf'
        : lesson.imageType !== 'pdf'
    );
    return sortAsc ? filtered : [...filtered].reverse();
  }, [lessons, sortAsc, mediaType]);

  if (!category) {
    return null;
  }

  return (
    <div className="flex flex-col h-full">
      <PageHeader
        title="Training"
        subtitle={category}
        avatar={{ src: global.me?.imageName, name: global.me?.name }}
        actions={
          canManage
            ? [
                {
                  id: 'add-media',
                  label: 'Add Media',
                  icon: <Plus className="size-4" />,
                  variant: 'primary',
                  onClick: () => setUploadVid(true),
                },
              ]
            : []
        }
      />

      <div className="flex-1 overflow-y-auto p-6">
        <div className="max-w-7xl mx-auto space-y-6">
          {/* Back navigation */}
          <button
            onClick={() => navigate('/v2/training')}
            className="flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft className="size-4" />
          </button>

          {/* Controls bar */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            {/* Media type tabs */}
            <Tabs
              value={mediaType}
              onValueChange={(val) => setMediaType(val as 'video' | 'document')}
            >
              <TabsList>
                <TabsTrigger value="video">Videos</TabsTrigger>
                <TabsTrigger value="document">Documents</TabsTrigger>
              </TabsList>
            </Tabs>

            {/* Display controls */}
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="icon"
                className={cn(
                  'size-9',
                  displayStyle === 'grid' && 'bg-primary/10 border-primary'
                )}
                onClick={() => setDisplayStyle('grid')}
              >
                <Grid3X3 className="size-4" />
              </Button>
              <Button
                variant="outline"
                size="icon"
                className={cn(
                  'size-9',
                  displayStyle === 'list' && 'bg-primary/10 border-primary'
                )}
                onClick={() => {
                  if (displayStyle === 'list') {
                    setSortAsc((prev) => !prev);
                  } else {
                    setDisplayStyle('list');
                  }
                }}
              >
                <List className="size-4" />
              </Button>
              {displayStyle === 'list' && (
                <span className="text-xs text-muted-foreground ml-1">
                  {sortAsc ? 'Oldest first' : 'Newest first'}
                </span>
              )}
            </div>
          </div>

          {/* Lessons grid/list */}
          {sortedLessons ? (
            sortedLessons.length > 0 ? (
              <div
                className={cn(
                  displayStyle === 'grid'
                    ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5'
                    : 'flex flex-col gap-3'
                )}
              >
                {sortedLessons.map((lesson) => (
                  <LessonCard
                    key={lesson.id}
                    lesson={lesson}
                    display={displayStyle}
                    refresh={refresh}
                    snackbar={(message: string, severity: string) => {
                      if (severity === 'error') {
                        toast.error(message);
                      } else {
                        toast.success(message);
                      }
                    }}
                  />
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-20 text-center">
                <p className="text-muted-foreground">
                  No {mediaType === 'document' ? 'documents' : 'videos'} in this
                  category.
                </p>
              </div>
            )
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
              {[0, 1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="rounded-lg overflow-hidden border border-border bg-card shadow-sm"
                >
                  <div className="aspect-video bg-muted animate-pulse" />
                  <div className="p-3 space-y-2">
                    <div className="h-4 w-3/4 bg-muted rounded animate-pulse" />
                    <div className="h-3 w-full bg-muted/60 rounded animate-pulse" />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <UploadLessonModal
        open={uploadVid}
        category={category}
        onClose={() => setUploadVid(false)}
        refresh={refresh}
        snackbar={(message: string, severity: string) => {
          if (severity === 'error') {
            toast.error(message);
          } else {
            toast.success(message);
          }
        }}
      />
    </div>
  );
}
